/**
 * Frontend allow-listed registry that mirrors the Stage 10 backend
 * message-key registry. Each entry maps a backend key to a safe display
 * template with named ``{placeholder}`` parameters, a severity, and a
 * short a11y-friendly description. Unknown keys must never be rendered
 * as raw strings; ``renderMessage`` returns a controlled fallback.
 */

const escapeText = (value) => {
  if (value == null) return "";
  const str = String(value);
  return str
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
};

export const COACHING_MESSAGE_REGISTRY = {
  // --- Today ---
  COACH_TITLE_TODAY_WORKOUT: { template: "Today's training" },
  COACH_TITLE_TODAY_WORKOUT_PURPOSE: { template: "Why this workout" },
  COACH_TITLE_TODAY_WORKOUT_FOCUS: { template: "What to focus on" },
  COACH_TITLE_TODAY_DAY_STATUS: { template: "Today" },
  COACH_TITLE_CURRENT_WEEK: { template: "This week" },
  COACH_TITLE_CURRENT_BLOCK: { template: "Training block" },
  COACH_TITLE_CURRENT_PHASE: { template: "Training phase" },
  COACH_TITLE_MAIN_LIMITER: { template: "Main limiter" },
  COACH_TITLE_CURRENT_READINESS: { template: "Readiness" },
  COACH_TITLE_UPCOMING_TRAINING: { template: "Upcoming training" },
  COACH_TITLE_LAST_SESSION_RESULT: { template: "Last session" },
  COACH_TITLE_SESSION_COMPLIANCE: { template: "Session compliance" },
  COACH_TITLE_SESSION_QUALITY: { template: "Session quality" },
  COACH_TITLE_WEEKLY_COMPLIANCE: { template: "Weekly compliance" },
  COACH_TITLE_BLOCK_COMPLIANCE: { template: "Block compliance" },
  COACH_TITLE_PLAN_CHANGE: { template: "Plan change" },
  COACH_TITLE_SESSION_MOVED: { template: "Session moved" },
  COACH_TITLE_SESSION_REMOVED: { template: "Session removed" },
  COACH_TITLE_SESSION_SUBSTITUTED: { template: "Session substituted" },
  COACH_TITLE_RECOVERY_INSERTED: { template: "Recovery inserted" },
  COACH_TITLE_REST_INSERTED: { template: "Rest inserted" },
  COACH_TITLE_PROGRESSION_HELD: { template: "Progression held" },
  COACH_TITLE_PROGRESSION_REGRESSED: { template: "Progression regressed" },
  COACH_TITLE_WEEK_CONVERTED_TO_RECOVERY:
    { template: "Week converted to recovery" },
  COACH_TITLE_BLOCK_REGENERATED: { template: "Block regenerated" },
  COACH_TITLE_SAFETY_SUSPENSION: { template: "Training suspended for safety" },
  COACH_TITLE_CHAT2_REASSESSMENT: { template: "Reassessment required" },
  COACH_TITLE_MISSING_INFORMATION: { template: "Missing information" },
  COACH_TITLE_LOW_CONFIDENCE: { template: "Low confidence" },
  COACH_TITLE_NO_PLAN: { template: "No plan yet" },
  COACH_TITLE_NO_WORKOUT_TODAY: { template: "No workout scheduled today" },
  COACH_TITLE_EXPLANATION_UNAVAILABLE:
    { template: "Explanation unavailable" },

  // --- Today status ---
  COACH_TODAY_TRAINING_DAY: { template: "Today is a training day." },
  COACH_TODAY_REST_DAY: { template: "Today is a rest day." },
  COACH_TODAY_RECOVERY_DAY: { template: "Today is a recovery day." },
  COACH_TODAY_EVENT_DAY:
    { template: "Today is your event day.",
       severity: "notice" },
  COACH_TODAY_PLAN_SUSPENDED:
    { template: "Training is currently suspended for safety.",
       severity: "critical" },
  COACH_TODAY_NO_SESSION:
    { template: "No session is scheduled for today." },
  COACH_TODAY_SESSION_SUMMARY:
    { template:
        "{workout_name} · {sport} · {role} · {duration_minutes} min" },
  COACH_TODAY_DOUBLE_SESSION:
    { template: "{session_count} sessions scheduled today." },

  // --- Purpose ---
  COACH_WORKOUT_SUPPORTS_PRIMARY_OBJECTIVE:
    { template: "This session supports your main goal: {objective}." },
  COACH_WORKOUT_TARGETS_MAIN_LIMITER:
    { template: "It targets the area currently limiting your performance: {limiter}." },
  COACH_WORKOUT_MAINTAINS_SECONDARY_OBJECTIVE:
    { template: "It keeps a secondary goal on track: {objective}." },
  COACH_WORKOUT_PROTECTS_PRIMARY_SPORT:
    { template: "It keeps your {primary_sport} fitness while other priorities take the lead." },
  COACH_WORKOUT_SUPPORTS_EVENT_PREPARATION:
    { template: "It supports your event preparation — {days_to_event} days to go." },
  COACH_WORKOUT_IS_RECOVERY_FOCUSED:
    { template: "This is an easier session to help you recover." },
  COACH_WORKOUT_IS_OPTIONAL:
    { template: "This session is optional — skip it if you need to." },

  // --- Focus ---
  COACH_FOCUS_PRESCRIPTION_METHOD:
    { template: "Prescription: {prescription_method}." },
  COACH_FOCUS_TARGET_INTENSITY:
    { template: "Target intensity: {intensity_class}." },
  COACH_FOCUS_DURATION:
    { template: "Duration: {duration_minutes} min." },
  COACH_FOCUS_INTERVAL_STRUCTURE:
    { template: "Interval structure: {interval_structure}." },
  COACH_FOCUS_CADENCE: { template: "Cadence: {cadence}." },
  COACH_FOCUS_PACE: { template: "Pace: {pace}." },
  COACH_FOCUS_HEART_RATE: { template: "Heart rate: {hr_zone}." },
  COACH_FOCUS_POWER: { template: "Power: {power_target}." },
  COACH_FOCUS_TECHNIQUE: { template: "Technique focus: {technique}." },
  COACH_FOCUS_RECOVERY: { template: "Recovery focus." },

  // --- Compliance / last session ---
  COACH_SESSION_COMPLETED_AS_PLANNED:
    { template: "Completed {workout_name} as planned." },
  COACH_SESSION_MINOR_VARIATION:
    { template: "Completed {workout_name} with minor variation." },
  COACH_SESSION_PARTIAL:
    { template: "Partially completed {workout_name}.",
       severity: "notice" },
  COACH_SESSION_SKIPPED:
    { template: "{workout_name} was skipped.",
       severity: "notice" },
  COACH_SESSION_AUTHORISED_SUBSTITUTION:
    { template: "Authorised substitution for {workout_name}." },
  COACH_SESSION_OVERCOMPLETED:
    { template: "{workout_name} was over-completed.",
       severity: "notice" },
  COACH_SESSION_DATA_INSUFFICIENT:
    { template: "Not enough data to evaluate this session." },
  COACH_DURATION_WITHIN_TOLERANCE: { template: "Duration within target." },
  COACH_DURATION_BELOW_TARGET: { template: "Duration below target." },
  COACH_DURATION_ABOVE_TARGET: { template: "Duration above target." },
  COACH_INTENSITY_WITHIN_TARGET: { template: "Intensity within target." },
  COACH_INTENSITY_BELOW_TARGET: { template: "Intensity below target." },
  COACH_INTENSITY_ABOVE_TARGET: { template: "Intensity above target." },
  COACH_SENSOR_DATA_UNAVAILABLE:
    { template: "{dimension} data was not available for this session." },
  COACH_ESTIMATED_POWER_NOT_USED:
    { template: "Estimated power was not used to score this workout." },
  COACH_RPE_HIGHER_THAN_EXPECTED:
    { template: "RPE was higher than expected." },
  COACH_WEEKLY_COMPLIANCE_SCORE:
    { template: "Weekly compliance score: {score}." },
  COACH_WEEKLY_KEY_SESSION_MISSED:
    { template: "{missed_count} key session(s) missed this week.",
       severity: "notice" },
  COACH_WEEKLY_UNPLANNED_HIGH_COST:
    { template: "{unplanned_count} unplanned hard session(s) this week." },
  COACH_BLOCK_COMPLIANCE_SCORE:
    { template: "Block compliance score: {score}." },
  COACH_BLOCK_COMPLIANCE_TREND:
    { template: "Compliance trend: {trend}." },
  COACH_COMPLIANCE_DATA_INSUFFICIENT:
    { template: "Not enough data to score compliance yet." },

  // --- Adaptation actions ---
  COACH_PLAN_NO_CHANGE_REQUIRED:
    { template: "No plan changes are required." },
  COACH_OPTIONAL_SESSION_REMOVED:
    { template: "An optional session was removed." },
  COACH_MISSED_SESSION_DROPPED:
    { template: "A missed session was removed from the plan." },
  COACH_KEY_SESSION_MOVED:
    { template: "A key session was moved." },
  COACH_KEY_SESSION_REPEATED:
    { template: "A key session was repeated." },
  COACH_SHORTER_VARIANT_SELECTED:
    { template: "A shorter session variant was selected." },
  COACH_LOWER_COST_SUBSTITUTION:
    { template: "A lower-cost substitution was applied." },
  COACH_RECOVERY_INSERTED: { template: "Recovery was inserted." },
  COACH_REST_INSERTED: { template: "Rest was inserted." },
  COACH_WEEKLY_VOLUME_REDUCED: { template: "Weekly volume was reduced." },
  COACH_HARD_SESSION_COUNT_REDUCED:
    { template: "The number of hard sessions was reduced." },
  COACH_PROGRESSION_HELD: { template: "Progression was held." },
  COACH_PROGRESSION_REGRESSED: { template: "Progression was regressed." },
  COACH_WEEK_CONVERTED_TO_CONSOLIDATION:
    { template: "This week was converted to a consolidation week." },
  COACH_WEEK_CONVERTED_TO_RECOVERY:
    { template: "This week was converted to a recovery week." },
  COACH_REMAINING_WEEK_REGENERATED:
    { template: "The remainder of the week was regenerated." },
  COACH_REMAINING_BLOCK_REGENERATED:
    { template: "The remainder of the block was regenerated." },
  COACH_ADAPTATION_APPLIED:
    { template: "Plan change applied." },
  COACH_ADAPTATION_PENDING_CONFIRMATION:
    { template: "Plan change is pending your confirmation." },
  COACH_ADAPTATION_APPLICATION_FAILED:
    { template: "Plan change could not be applied.",
       severity: "warning" },

  // --- Safety / reassessment ---
  COACH_TRAINING_SUSPENDED_FOR_SAFETY:
    { template: "Training is suspended for safety.",
       severity: "critical" },
  COACH_PROFESSIONAL_REVIEW_REQUIRED:
    { template: "Professional review is required before resuming.",
       severity: "critical" },
  COACH_CHAT2_REASSESSMENT_REQUIRED:
    { template: "A coaching reassessment is required.",
       severity: "high" },
  COACH_PHASE_CHANGE_NOT_APPLIED_HERE:
    { template: "Phase changes are not applied on this screen." },
  COACH_LIMITER_CHANGE_NOT_APPLIED_HERE:
    { template: "Limiter changes are not applied on this screen." },
  COACH_NO_MEDICAL_DIAGNOSIS:
    { template:
        "Endurava does not diagnose medical conditions or prescribe treatment." },

  // --- Missing information ---
  COACH_MISSING_PROFILE_INFORMATION:
    { template: "Profile information is missing: {field}." },
  COACH_MISSING_GOAL_INFORMATION:
    { template: "Goal information is missing." },
  COACH_MISSING_AVAILABILITY:
    { template: "Training availability is missing." },
  COACH_MISSING_SENSOR_DATA:
    { template: "Sensor data is missing ({dimension})." },
  COACH_MISSING_READINESS_DATA:
    { template: "Recent readiness data is missing." },
  COACH_MISSING_COMPLIANCE_DATA:
    { template: "Compliance data is not yet available." },
  COACH_LOW_CONFIDENCE:
    { template:
        "Confidence is low ({section}); more data will improve accuracy." },
  COACH_EXPLANATION_SOURCE_UNAVAILABLE:
    { template: "Some source information is unavailable." },

  // --- Section summaries ---
  COACH_CURRENT_PHASE:
    { template: "Current phase: {phase}." },
  COACH_MAIN_LIMITER:
    { template: "Main limiter: {limiter}." },
  COACH_CURRENT_WEEK_SUMMARY:
    { template: "Week {week_number} — {week_type}." },
  COACH_CURRENT_BLOCK_SUMMARY:
    { template: "{phase} block, {block_length_weeks} weeks." },
  COACH_UPCOMING_SESSION:
    { template: "{workout_name} on {date} ({duration_minutes} min)." },
  COACH_CURRENT_READINESS:
    { template: "Readiness: {classification}." },
  COACH_NO_PLAN_AVAILABLE:
    { template: "No plan is available yet." },
  COACH_NO_WORKOUT_TODAY:
    { template: "No workout is scheduled for {date}." },
};

/**
 * Interpolate a message key against structured params. Returns
 * ``{ text, key, isFallback, severity }``. Missing parameters render as
 * a bounded ``—`` placeholder; unknown keys render a controlled
 * fallback and are flagged for callers.
 */
export const renderMessage = (key, params = {}) => {
  const entry = COACHING_MESSAGE_REGISTRY[key];
  if (!entry) {
    return {
      key, text: "Unavailable", isFallback: true,
      severity: entry?.severity || "info",
    };
  }
  const text = entry.template.replace(/\{(\w+)\}/g, (_match, name) => {
    const raw = params[name];
    if (raw == null || raw === "") return "—";
    return escapeText(raw);
  });
  return { key, text, isFallback: false,
             severity: entry.severity || "info" };
};

export const isRegisteredMessage = (key) =>
  Object.prototype.hasOwnProperty.call(COACHING_MESSAGE_REGISTRY, key);

export const registrySize = () =>
  Object.keys(COACHING_MESSAGE_REGISTRY).length;

export const _internal = { escapeText };
